// The pages chosen in "Add pages from a PDF": typed as pages and ranges ("4-6, 9, 12-14"), kept in order and each
// once, and named the same way ("PDF pages 4–6, 9, 12–14"). Kept apart from the popup, which loads pdf.js, so the
// Description page can name added pages without it.
import { SOURCE_LINE_PREFIX } from './importChecks';

/** The pages as runs of consecutive pages: [4, 5, 6, 9] gives [[4, 6], [9, 9]]. */
export function pageRuns(pages: number[]): [number, number][] {
    const runs: [number, number][] = [];
    for (const page of [...new Set(pages)].sort((a, b) => a - b)) {
        const last = runs[runs.length - 1];
        if (last && page === last[1] + 1) last[1] = page;
        else runs.push([page, page]);
    }
    return runs;
}

/** "4–6, 9", with the dash given between a run's first and last page (a hyphen where the teacher types them). */
export function pageRunsText(pages: number[], dash = '–'): string {
    return pageRuns(pages).map(([first, last]) => (first === last ? String(first) : `${first}${dash}${last}`)).join(', ');
}

/** "PDF page 4", "PDF pages 4–6" or "PDF pages 4–6, 9". */
export function pdfPagesName(pages: number[]): string {
    return `${new Set(pages).size === 1 ? 'PDF page' : 'PDF pages'} ${pageRunsText(pages)}`;
}

/** The printed page numbers of the pages, run by run ("10–12, 15"); null when a run's first or last page has none. */
export function printedRunsText(pages: number[], label: (page: number) => string | null): string | null {
    const parts = pageRuns(pages).map(([first, last]) => {
        const [from, to] = [label(first), label(last)];
        return from && to ? (from === to ? from : `${from}–${to}`) : null;
    });
    return parts.length && parts.every(Boolean) ? parts.join(', ') : null;
}

/** The pages' source line: the file and the pages, range by range (with their printed numbers when every range has them). */
export function pagesSourceLine(fileName: string, pages: { pageNumber: number; label: string | null }[]): string {
    const labels = new Map(pages.map(page => [page.pageNumber, page.label]));
    const printed = printedRunsText([...labels.keys()], page => labels.get(page) ?? null);
    return `${SOURCE_LINE_PREFIX}${fileName}, ${pdfPagesName([...labels.keys()])}${printed ? ` (printed ${printed})` : ''}`;
}

/** The most pages one import holds, as the server keeps them (MAX_PAGES in service/verbaschola_pdf_import_checks.py). */
export const MAX_IMPORT_PAGES = 500;
/** The most warnings one import keeps, as the server does (MAX_WARNINGS there); known only once the pages are read. */
export const MAX_IMPORT_WARNINGS = 2000;
/** The longest text of a page as read that one import keeps, in characters as the server counts them (MAX_PAGE_TEXT). */
export const MAX_PAGE_TEXT = 60000;
/**
 * The longest source line one import holds, in characters as the server counts them (normalize_import in
 * service/verbaschola_pdf_import_checks.py): pages in many separate ranges, or a very long file name, can pass it.
 */
export const MAX_SOURCE_LINE = 1000;

/** Why the pages cannot be one import because their source line is too long; '' when it fits. */
export function sourceLineError(line: string): string {
    const length = Array.from(line).length;
    return length > MAX_SOURCE_LINE
        ? `The file name and these pages would make a source line of ${length} characters; one import holds at most ${MAX_SOURCE_LINE}. Use fewer separate ranges, or add the pages in two goes.`
        : '';
}

/**
 * The pages a teacher typed: page numbers and ranges, such as "4-6, 9, 12-14", between commas, semicolons or spaces
 * (Chinese punctuation and full-width digits too). They come back in order and each once, or with why the text is not
 * such a list. A page or range given twice, or ranges that overlap, are simply joined. More than MAX_IMPORT_PAGES pages
 * is an error, with the pages.
 */
export function parsePages(text: string, pageCount: number): { pages: number[]; error: string } {
    const items = text.normalize('NFKC').replace(/\s*[-\u2010-\u2015~\u301C]\s*/g, '-').split(/[\s,;\u3001]+/).filter(Boolean);
    const pages = new Set<number>();
    for (const item of items) {
        const match = /^(\d+)(?:-(\d+))?$/.exec(item);
        if (!match) return { pages: [], error: `"${item}" is not a page or a range of pages. Use PDF page numbers, such as 4-6, 9.` };
        const first = Number(match[1]);
        const last = Number(match[2] ?? match[1]);
        if (first > last) return { pages: [], error: `In ${item}, the last page comes before the first.` };
        if (first < 1 || last > pageCount) return { pages: [], error: `This PDF has pages 1 to ${pageCount}.` };
        for (let page = first; page <= last; page += 1) pages.add(page);
    }
    const sorted = [...pages].sort((a, b) => a - b);
    // Too many pages still come back, so a section or page that added them can take them out again.
    if (pages.size > MAX_IMPORT_PAGES) return { pages: sorted, error: `That is ${pages.size} pages; at most ${MAX_IMPORT_PAGES} can be added at once. Add them in two goes.` };
    return { pages: sorted, error: '' };
}
